import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { rapportService } from '../../api/services';
import { EtatAnnuelData } from '../../types';
import { formatTND } from '../../utils/formatters';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const PIE_COLORS = ['#60a5fa', '#fbbf24', '#1e40af'];

export function EtatAnnuel() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const [annee, setAnnee] = useState(currentYear);
  const [data, setData] = useState<EtatAnnuelData | null>(null);
  const [loading, setLoading] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const result = await rapportService.getEtatAnnuel(annee);
      setData(result);
    } catch (error) {
      toast.error(t.messages.loadError);
      setData({
        annee,
        caTotal: 0,
        caMensuel: [],
        tvaCollectee: 0,
        nombreFactures: 0,
        nombreDevis: 0,
        nombreAvoirs: 0,
        topClients: [],
        repartitionTva: [],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t.rapports.annuel.title}</h1>
          <p className="page-subtitle">Synthèse annuelle de l'activité</p>
        </div>
        {data && (
          <button onClick={() => window.print()} className="btn-outline no-print">
            <PrinterIcon className="h-4 w-4 mr-1" />
            {t.common.print}
          </button>
        )}
      </div>

      <div className="card no-print">
        <div className="flex gap-3 items-end">
          <div>
            <label className="label">{t.rapports.annuel.annee}</label>
            <select
              value={annee}
              onChange={(e) => setAnnee(parseInt(e.target.value))}
              className="input-field w-32"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button onClick={handleGenerate} className="btn-primary">
            {t.rapports.tva.generer}
          </button>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {data && !loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t.rapports.annuel.caTotal}</p>
              <MoneyDisplay amount={data.caTotal} size="lg" className="text-primary-600 mt-1" />
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t.rapports.annuel.tvaCollectee}</p>
              <MoneyDisplay amount={data.tvaCollectee} size="lg" className="mt-1" />
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t.rapports.annuel.nombreFactures}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.nombreFactures}</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t.rapports.annuel.nombreDevis}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.nombreDevis}</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t.rapports.annuel.nombreAvoirs}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.nombreAvoirs}</p>
            </div>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t.rapports.annuel.evolutionCA}
            </h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.caMensuel}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value: any) => [formatTND(Number(value)), 'CA']}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Bar dataKey="montant" fill="#1e40af" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {data.repartitionTva.length > 0 && (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.rapports.annuel.repartitionTva}
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.repartitionTva}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="montant"
                        nameKey="tauxTva"
                        label={({ name, percent }: any) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                      >
                        {data.repartitionTva.map((_, idx) => (
                          <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: any) => formatTND(Number(value))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {data.topClients.length > 0 && (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.dashboard.topClients}
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t.dashboard.client}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          {t.dashboard.montant}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          {t.dashboard.nombreFactures}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.topClients.map((client, idx) => (
                        <tr key={client.clientId}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </span>
                              <span className="text-sm font-medium">{client.raisonSociale}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <MoneyDisplay amount={client.totalCA} size="sm" />
                          </td>
                          <td className="px-4 py-3 text-right text-sm">{client.nombreFactures}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EtatAnnuel;
